import React, { useEffect, useState } from 'react';
import { SalesWeeklyIncentive, fetchWeeklyIncentive } from '../../../Reports/reportsApi';
import { Card, Badge, Button, Loader } from '../../../ui';
import ProgressBar from './ui-pending/ProgressBar';
import './ui-pending/uiPending.scss';
import './sales.scss';

/**
 * A week's incentive (Sunday to Saturday): the plan the agent was on that
 * week, worked out by artium-reports against the week's numbers (so far, for
 * the current week) with the same maths as the plan calculator. Shows what's
 * earned, every gate with how far there is to go, and the payout lines.
 * Past weeks show what was earned then, on the plan in force that week.
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Weeks are named by their Sunday, as in artium-reports.
const DAY = 864e5;
const parseIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const toIso = (d: Date): string => d.toISOString().slice(0, 10);
const istToday = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const weekStartOf = (iso: string): string => toIso(new Date(parseIso(iso).getTime() - parseIso(iso).getUTCDay() * DAY));
const shiftWeek = (weekStart: string, delta: number): string => toIso(new Date(parseIso(weekStart).getTime() + delta * 7 * DAY));

const inr = (n: number): string => `₹${Math.round(n).toLocaleString('en-IN')}`;
const dayName = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${DAYS[date.getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
};
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;
const ordinal = (n: number): string => {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  return `${n}${teen ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/** One gate: label, a bar of actual against required, and what's left. */
const Gate: React.FC<{ label: string; met: boolean; skipped?: boolean; pct: number; value: string; left: string | null }> = ({
  label, met, skipped, pct, value, left,
}) => (
  <div className="sales-gate">
    <div className="sales-line">
      <span className="ds-strong">
        <span className={`sales-gate__icon sales-gate__icon--${skipped ? 'skipped' : met ? 'met' : 'open'}`}>
          {skipped ? '–' : met ? '✓' : '✗'}
        </span>{' '}
        {label}
      </span>
      <span className="ds-num">{value}</span>
    </div>
    <ProgressBar value={pct} tone={skipped ? 'neutral' : met ? 'success' : 'primary'} label={label} />
    {left && <div className={`sales-caption ${met ? 'sales-text--success' : 'sales-text--warning'}`}>{left}</div>}
  </div>
);

export const ThisWeeksIncentive: React.FC<{
  /** Admin only: whose. An agent always gets their own. */
  email?: string | null;
  /** First name, for an admin looking at someone: "Kuntal's incentive this week". */
  name?: string;
  /**
   * Any day in the week to open on (the page's range end); it opens on that
   * week's Sunday, never later than this week. The arrows move from there.
   */
  initialDay?: string;
}> = ({ email, name, initialDay }) => {
  const current = weekStartOf(istToday());
  const opening = initialDay ? weekStartOf(initialDay) : current;
  const [week, setWeek] = useState(opening > current ? current : opening);
  const [data, setData] = useState<SalesWeeklyIncentive | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // A new range on the page moves the card to that range's week.
  useEffect(() => { setWeek(opening > current ? current : opening); }, [opening, current]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    fetchWeeklyIncentive(email ?? undefined, week)
      .then((d) => { if (live) setData(d); })
      .catch((err: any) => { if (live) { setData(null); setError(err?.message || "Could not load the week's incentive"); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [email, week]);

  const isCurrent = week === current;
  const whose = name ? `${name}'s incentive` : 'Incentive';
  const title = isCurrent ? (name ? `${whose} this week` : "This week's incentive") : `${whose} · week of ${dayName(week)}`;

  const meta = (
    <span className="sales-incentive__meta ds-text-muted">
      {data && <>{data.label}{data.inProgress ? ` · so far, through ${dayName(data.through)}` : ''}</>}
      {data?.plan && <Badge tone="neutral">{data.plan.name}</Badge>}
      <span className="sales-week" role="group" aria-label="Week (Sun–Sat)">
        <Button variant="secondary" size="sm" title="Previous week" aria-label="Previous week"
          disabled={loading} onClick={() => setWeek(shiftWeek(week, -1))}>‹</Button>
        <Button variant="secondary" size="sm" title="Next week" aria-label="Next week"
          disabled={loading || isCurrent} onClick={() => setWeek(shiftWeek(week, 1))}>›</Button>
      </span>
      {!isCurrent && <Button variant="ghost" size="sm" onClick={() => setWeek(current)}>This week</Button>}
    </span>
  );
  const shell = (body: React.ReactNode) => (
    <Card title={title} actions={meta}>{body}</Card>
  );

  if (loading) return shell(<Loader label="Loading the week's incentive…" />);
  if (error || !data) return shell(<p className="sales-note ds-text-muted">{error}</p>);

  if (!data.isAgent) {
    return shell(
      <p className="sales-note ds-text-muted">
        <span className="ds-strong">{data.email}</span> isn&apos;t on the Sales Agents list, so there&apos;s no incentive plan to work out.
      </p>,
    );
  }
  if (!data.plan || !data.breakdown) {
    return shell(
      <p className="sales-note ds-text-muted">
        No incentive plan {isCurrent ? 'for this week yet' : 'was in force this week'}. A sales admin assigns one on Sales → Targets &amp; Incentives.
      </p>,
    );
  }

  const { plan, actuals, breakdown: b } = data;
  const demos = Object.values(actuals.byCountry).reduce((n, c) => n + (c?.assigned ?? 0), 0);
  const toPayoutStart = Math.max(0, plan.conversionPayoutFrom - actuals.conversions);
  const metCount = b.gates.filter((g) => g.met).length;

  const gateRows = b.gates.map((g) => {
    if (g.key === 'revenue') {
      const left = g.met ? 'Cleared' : `${inr(g.required - (g.actual ?? 0))} more to go`;
      return <Gate key={g.key} label="Revenue" met={g.met} pct={((g.actual ?? 0) / Math.max(1, g.required)) * 100}
        value={`${inr(g.actual ?? 0)} / ${inr(g.required)}`} left={left} />;
    }
    if (g.key === 'conversions') {
      const left = g.met ? 'Cleared' : `${plural(g.required - (g.actual ?? 0), 'more conversion')} to go`;
      return <Gate key={g.key} label="Conversions" met={g.met} pct={((g.actual ?? 0) / Math.max(1, g.required)) * 100}
        value={`${g.actual} / ${g.required}`} left={left} />;
    }
    // Conversion % for a lead country.
    const country = g.key.slice(4) as 'India' | 'USA' | 'ROW';
    const c = actuals.byCountry[country];
    if (g.skipped || !c) {
      return <Gate key={g.key} label={g.label} met skipped pct={0} value={`needs ${g.required}%`} left="No leads from here yet, so it doesn't apply" />;
    }
    // Conversions needed at today's leads to reach the bar.
    const needed = Math.max(0, Math.ceil((g.required / 100) * c.assigned - c.converted));
    const left = g.met ? 'Cleared' : `${plural(needed, 'more conversion')} from ${c.assigned} leads to reach ${g.required}%`;
    return <Gate key={g.key} label={g.label} met={g.met} pct={((g.actual ?? 0) / Math.max(1, g.required)) * 100}
      value={`${g.actual}% / ${g.required}%`} left={left} />;
  });

  const line = (label: React.ReactNode, amount: React.ReactNode, muted = false) => (
    <div className={`sales-line${muted ? ' ds-text-subtle' : ''}`}>
      <span>{label}</span><span className="ds-num">{amount}</span>
    </div>
  );
  // While a gate is open, show what each line would pay, greyed.
  const would = !b.eligible;

  return shell(
    <div className="row g-4">
      <div className="col-12 col-md-4">
        <div className="sls-stat__label">{data.inProgress ? 'Earned so far' : 'Earned'}</div>
        <div className={`sales-earned${b.eligible ? ' sales-text--success' : ''}`}>{inr(b.total)}</div>
        {b.eligible ? (
          <div className="sales-note sales-text--success">Every gate cleared — it all counts.</div>
        ) : (
          <div className="sales-note">
            <span className="sales-text--danger">{plural(b.gates.length - metCount, 'gate')} {data.inProgress ? 'still open' : 'not met'},</span> so nothing is paid{data.inProgress ? ' yet' : ''}.
            {b.potentialTotal > 0 && (data.inProgress
              ? <> Clear {b.gates.length - metCount === 1 ? 'it' : 'them'} and this week pays <span className="ds-strong">{inr(b.potentialTotal)}</span> at today&apos;s numbers.</>
              : <> With every gate cleared it would have paid <span className="ds-strong">{inr(b.potentialTotal)}</span>.</>)}
          </div>
        )}
        {data.demoTarget != null && (
          <div className="sales-note ds-text-muted">Demos: <span className="ds-strong">{demos}</span> of a {data.demoTarget} target</div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <div className="sales-subheading">Gates — all must be met · {metCount} of {b.gates.length}</div>
        <div className="ds-stack">{gateRows}</div>
      </div>

      <div className="col-12 col-md-4">
        <div className="sales-subheading">{would ? 'What it would pay' : 'Payout'}</div>
        <div className="ds-stack">
          {line(
            <>Conversion payout{toPayoutStart > 0 && <span className="ds-text-muted"> · starts at the {ordinal(plan.conversionPayoutFrom)}, {toPayoutStart} to go</span>}</>,
            inr(would ? (actuals.conversions >= plan.conversionPayoutFrom ? plan.conversionPayoutBase + plan.conversionPayoutPerExtra * (actuals.conversions - plan.conversionPayoutFrom) : 0) : b.conversionPayout),
            would,
          )}
          {line(`${plan.revenuePct}% of revenue`, inr(would ? Math.round((actuals.revenue * plan.revenuePct) / 100) : b.revenuePayout), would)}
          {line(
            <>Session bonus{plan.sessionTiers[0] && (
              <span className="ds-text-muted"> · per package, from {plan.sessionTiers[0].minSessions} sessions → {inr(plan.sessionTiers[0].amount)}</span>
            )}</>,
            data.sessionBonusTracked ? inr(b.sessionBonus) : <span className="ds-text-muted" title="Package session counts aren't in the data yet">not tracked yet</span>,
            would,
          )}
          <div className="sales-line sales-line--total">
            <span className="ds-strong">{would ? 'Would be' : 'Total'}</span>
            <span className={`ds-strong ds-num${would ? ' ds-text-muted' : ' sales-text--success'}`}>{inr(would ? b.potentialTotal : b.total)}</span>
          </div>
        </div>
      </div>
    </div>,
  );
};

export default ThisWeeksIncentive;
